/**
 * Generates a list of validation rules for a given set of fields
 * 
 * @param {Object} fields 
 * @param {Object} existingAnswers
 * @returns {Object}
 */
export const generateFieldValidationRules = (fields, existingAnswers = {}) => fields.reduce(
    (rules, field) => ({
        ...rules,
        ...generateValidationRulesForField(field, existingAnswers[field.id] !== undefined)
    }),
    {}
)

/**
 * Generates validation rules for address lookup fields
 * 
 * @param {Object} field 
 * @returns {Object}
 */
const generateAddressLookupValidationRules = (field) => {
    const fields = [
        {
            id         : `${field.id}.address.line_1`,
            field_type : { value: 'text' },
            required   : field.required
        },
        {
            id         : `${field.id}.address.line_2`,
            field_type : { value: 'text' },
            required   : false
        },
        {
            id         : `${field.id}.address.county`,
            field_type : { value: 'text' },
            required   : field.required
        },
        {
            id         : `${field.id}.address.country`,
            field_type : { value: 'text' },
            required   : field.required
        },
        {
            id         : `${field.id}.address.postcode`,
            field_type : { value: 'text' },
            required   : field.required
        },
        {
            id         : `${field.id}.address.town_or_city`,
            field_type : { value: 'text' },
            required   : field.required
        }
    ]

    return fields.reduce((rules, field) => ({
        ...rules,
        ...generateValidationRulesForField(field)
    }), {})
}

/**
 * Generates a list of validation rules for a single field
 * 
 * @param {Object}  field 
 * @param {Boolean} hasExistingAnswer
 * @returns {Object}
 */
export const generateValidationRulesForField = (field, hasExistingAnswer = false) => {

    //Only generate validation rules for fields which should be submitted
    const fieldVisibility = field?.field_visibility?.value
    if(
        ['viewable_by_user', 'admin_only'].includes(fieldVisibility)
        || (
            hasExistingAnswer
            && fieldVisibility === 'assignable_once_by_user'
        )
    ){
        return {}
    }


    if(field.field_type.value === 'address-lookup'){
        return generateAddressLookupValidationRules(field)
    }

    const fieldRuleKey = `fields.${field.id}`

    const rules = {
        [fieldRuleKey]: [
            ...getRequiredRulesForField(field) ?? [],
            ...getFileRulesForField(field) ?? [],
            ...getDropdownRulesForField(field) ?? []
        ],
    }

    if(field.multiple){
        //A multiple field cannot be a file so we don't need to append the rules
        rules[`${fieldRuleKey}.*`] = [
            ...getRequiredRulesForField(field) ?? [],
            ...getDropdownRulesForField(field, true) ?? []
        ]
    }

    return rules
}

/**
 * Generates validation rules for a required field
 * 
 * @param {Object} field 
 * @returns {Array|undefined}
 */
export const getRequiredRulesForField = (field) => {
    return field.required
        ? ['required']
        : ['nullable']
}

/**
 * Generates validation rules for a file field
 * 
 * @param {String} field 
 * @returns {Array|undefined}
 */
export const getFileRulesForField = (field) => {
    if(field.field_type.value === 'file'){
        return ['file:true']
    }
}

/**
 * Generates validation rules for a dropdown
 * 
 * @param {Object} field 
 * @param {Boolean} multiple
 * @returns {Array|undefined}
 */
export const getDropdownRulesForField = (field, multiple = false) => {
    if(field.field_type.value !== 'dropdown'){
        return undefined
    }

    let rules = []

    //If we're generating rules for inner fields
    //Or if we don't have a multiple field and aren't generating rules for the inner fields
    if(multiple || (!multiple && !field.multiple)){
        const optionIds = field.options.map(option => option.id)

        rules.push(`in:${optionIds.join(',')}`)
    }

    return rules
}